// Maps every exercise slug to the muscles it trains.
// Primary = full credit (1.0); secondary = partial credit (0.5).
// Also defines per-muscle metadata: size factor (affects recovery time) and
// display label. Used by the fatigue model and heatmap UI.

export type MuscleId =
  | "chest"
  | "front_delts"
  | "side_delts"
  | "rear_delts"
  | "biceps"
  | "triceps"
  | "forearms"
  | "abs"
  | "obliques"
  | "traps"
  | "lats"
  | "lower_back"
  | "quads"
  | "hamstrings"
  | "glutes"
  | "calves";

export interface MuscleMeta {
  id: MuscleId;
  label: string;
  /** 0.5 (tiny — calves, forearms) … 1.5 (large — quads, back). Bigger = slower recovery. */
  size: number;
  side: "front" | "back" | "both";
}

export const MUSCLES: Record<MuscleId, MuscleMeta> = {
  chest:       { id: "chest",       label: "Chest",         size: 1.2, side: "front" },
  front_delts: { id: "front_delts", label: "Front Delts",   size: 0.8, side: "front" },
  side_delts:  { id: "side_delts",  label: "Side Delts",    size: 0.8, side: "both"  },
  rear_delts:  { id: "rear_delts",  label: "Rear Delts",    size: 0.7, side: "back"  },
  biceps:      { id: "biceps",      label: "Biceps",        size: 0.7, side: "front" },
  triceps:     { id: "triceps",     label: "Triceps",       size: 0.8, side: "back"  },
  forearms:    { id: "forearms",    label: "Forearms",      size: 0.5, side: "both"  },
  abs:         { id: "abs",         label: "Abs",           size: 0.7, side: "front" },
  obliques:    { id: "obliques",    label: "Obliques",      size: 0.6, side: "front" },
  traps:       { id: "traps",       label: "Traps",         size: 0.9, side: "back"  },
  lats:        { id: "lats",        label: "Lats",          size: 1.3, side: "back"  },
  lower_back:  { id: "lower_back",  label: "Lower Back",    size: 1.0, side: "back"  },
  quads:       { id: "quads",       label: "Quads",         size: 1.5, side: "front" },
  hamstrings:  { id: "hamstrings",  label: "Hamstrings",    size: 1.3, side: "back"  },
  glutes:      { id: "glutes",      label: "Glutes",        size: 1.4, side: "back"  },
  calves:      { id: "calves",      label: "Calves",        size: 0.6, side: "back"  },
};

export const ALL_MUSCLES = Object.values(MUSCLES);

export interface ExerciseMuscleMap {
  primary: MuscleId[];
  secondary: MuscleId[];
}

/** Curated map for every exercise slug shipped in PROGRAM. Coach/manual
 *  exercises fall back to keyword inference (see `inferMusclesByName`). */
export const EXERCISE_MUSCLES: Record<string, ExerciseMuscleMap> = {
  // Upper push
  "barbell-bench-press":     { primary: ["chest"], secondary: ["triceps", "front_delts"] },
  "incline-db-press":        { primary: ["chest", "front_delts"], secondary: ["triceps"] },
  "incline-barbell-press":   { primary: ["chest", "front_delts"], secondary: ["triceps"] },
  "weighted-dip":            { primary: ["chest", "triceps"], secondary: ["front_delts"] },
  "cable-crossover":         { primary: ["chest"], secondary: ["front_delts"] },
  "seated-db-shoulder-press":{ primary: ["front_delts", "side_delts"], secondary: ["triceps"] },
  "cable-lateral-raise":     { primary: ["side_delts"], secondary: [] },
  "cable-face-pull":         { primary: ["rear_delts"], secondary: ["traps"] },
  "tricep-pushdown":         { primary: ["triceps"], secondary: [] },
  "overhead-tricep-extension":{ primary: ["triceps"], secondary: [] },
  // Upper pull
  "barbell-row":             { primary: ["lats"], secondary: ["rear_delts", "biceps", "traps"] },
  "seated-cable-row":        { primary: ["lats"], secondary: ["rear_delts", "biceps"] },
  "lat-pulldown":            { primary: ["lats"], secondary: ["biceps"] },
  "weighted-pull-up":        { primary: ["lats"], secondary: ["biceps", "rear_delts"] },
  "chin-up":                 { primary: ["lats", "biceps"], secondary: [] },
  "ez-bar-curl":             { primary: ["biceps"], secondary: ["forearms"] },
  "hammer-curl":             { primary: ["biceps", "forearms"], secondary: [] },
  // Lower posterior
  "deadlift":                { primary: ["hamstrings", "glutes", "lower_back"], secondary: ["lats", "traps", "forearms"] },
  "romanian-deadlift":       { primary: ["hamstrings", "glutes"], secondary: ["lower_back"] },
  "rdl-heavy":               { primary: ["hamstrings", "glutes"], secondary: ["lower_back"] },
  "hip-thrust":              { primary: ["glutes"], secondary: ["hamstrings"] },
  "leg-curl":                { primary: ["hamstrings"], secondary: [] },
  // Lower quad-dominant
  "barbell-back-squat":      { primary: ["quads", "glutes"], secondary: ["lower_back", "hamstrings"] },
  "leg-press":               { primary: ["quads"], secondary: ["glutes"] },
  "single-leg-press":        { primary: ["quads", "glutes"], secondary: ["hamstrings"] },
  "bulgarian-split-squat":   { primary: ["quads", "glutes"], secondary: ["hamstrings"] },
  "walking-lunge-db":        { primary: ["quads", "glutes"], secondary: ["hamstrings"] },
  "leg-extension":           { primary: ["quads"], secondary: [] },
  "standing-calf-raise":     { primary: ["calves"], secondary: [] },
  "seated-calf-raise":       { primary: ["calves"], secondary: [] },
  // Core
  "plank":                   { primary: ["abs"], secondary: ["obliques"] },
  "ab-wheel-rollout":        { primary: ["abs"], secondary: ["lats"] },
  "sit-up":                  { primary: ["abs"], secondary: [] },
  "v-up":                    { primary: ["abs"], secondary: [] },
  "toes-to-bar":             { primary: ["abs"], secondary: ["lats"] },
  "hanging-knee-raise":      { primary: ["abs"], secondary: [] },
  "dead-bug":                { primary: ["abs"], secondary: [] },
  // Recovery / mobility — no fatigue contribution
  "hip-flexor-stretch":      { primary: [], secondary: [] },
  "cat-cow":                 { primary: [], secondary: [] },
  "thoracic-rotation":       { primary: [], secondary: [] },
  "zone2-walk":              { primary: [], secondary: [] },
  "foam-roll":               { primary: [], secondary: [] },
};

/** Loose keyword inference for AI/manual exercises not in the curated map. */
export function inferMusclesByName(slug: string, name: string): ExerciseMuscleMap {
  const direct = EXERCISE_MUSCLES[slug];
  if (direct) return direct;
  const h = `${slug} ${name}`.toLowerCase().replace(/[_\-]+/g, " ");
  const primary = new Set<MuscleId>();
  const secondary = new Set<MuscleId>();
  const add = (set: Set<MuscleId>, m: MuscleId) => set.add(m);

  // CHEST
  if (/bench|chest|pec |pecs|pectoral|fly|flye|push ?up|press up|dip(?!lo)|svend|squeeze press/.test(h)) {
    add(primary, "chest"); add(secondary, "triceps"); add(secondary, "front_delts");
  }
  // SHOULDERS
  if (/incline.*press|shoulder press|overhead press|ohp|military|arnold|z press|landmine press|pike push|handstand/.test(h)) {
    add(primary, "front_delts"); add(secondary, "triceps");
  }
  if (/front raise|plate raise/.test(h)) add(primary, "front_delts");
  if (/lateral raise|side raise|lat raise|y raise|cable raise|leaning raise/.test(h)) add(primary, "side_delts");
  if (/rear delt|face pull|reverse fly|reverse pec|bent[- ]?over raise|rear raise|prone raise|w raise/.test(h)) add(primary, "rear_delts");
  // ARMS
  if (/tricep|push ?down|skull|kickback|jm press|close grip|diamond push|french press|overhead extension/.test(h)) add(primary, "triceps");
  if (/bicep|curl(?! ?up)|preacher|hammer|concentration|zottman|drag curl|spider curl|21s/.test(h)) add(primary, "biceps");
  if (/forearm|wrist curl|farmer|reverse curl|grip|pinch|dead ?hang/.test(h)) add(primary, "forearms");
  // BACK
  if (/pull ?up|chin ?up|pulldown|row|lat\b|meadows|pendlay|seal row|t bar|inverted row|australian pull/.test(h)) {
    add(primary, "lats"); add(secondary, "biceps"); add(secondary, "rear_delts");
  }
  if (/trap|shrug|upright row|rack pull|snatch grip|farmer carry|farmers walk/.test(h)) add(primary, "traps");
  if (/deadlift|good morning|back extension|hyperextension|jefferson|reverse hyper|superman/.test(h)) {
    add(primary, "lower_back"); add(secondary, "hamstrings"); add(secondary, "glutes");
  }
  // LEGS
  if (/squat|leg press|lunge|step ?up|sissy|hack squat|zercher|goblet|split squat|pistol|shrimp squat|jump squat|box jump/.test(h)) {
    add(primary, "quads"); add(secondary, "glutes");
  }
  if (/leg extension|knee extension/.test(h)) add(primary, "quads");
  if (/hip thrust|glute bridge|glute kickback|hip extension|abductor|abduction|clamshell|monster walk|banded walk|kas glute|frog pump|donkey kick/.test(h)) add(primary, "glutes");
  if (/adductor|adduction|inner thigh|copenhagen|sumo/.test(h)) { add(primary, "glutes"); add(secondary, "quads"); }
  if (/rdl|romanian|leg curl|nordic|stiff ?leg|glute ham|ghr|hamstring/.test(h)) add(primary, "hamstrings");
  if (/calf|calves|toe raise|donkey raise|tibialis|heel raise/.test(h)) add(primary, "calves");
  // CORE
  if (/\babs?\b|crunch|sit ?up|plank|hollow|knee raise|leg raise|v ?up|ab ?wheel|rollout|dead ?bug|bird ?dog|toes to bar|hanging|mountain climb|dragon flag|windshield/.test(h)) add(primary, "abs");
  if (/oblique|side bend|wood ?chop|russian twist|torso rotation|torso twist|pallof|anti ?rotation|cable rotation|side plank|windmill|around the world/.test(h)) add(primary, "obliques");

  // Remove anything also in primary
  for (const m of primary) secondary.delete(m);
  return { primary: Array.from(primary), secondary: Array.from(secondary) };
}

